import React, { useEffect, useRef, useState } from 'react';
import { ComponentConfig, FieldLabel } from '@measured/puck';
import {
  MarginProps,
  marginFields,
} from '../../../../page-builder/puck/reusable-props/NewMargin';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../../services/constants';
import CustomTooltip from '../../../../components/common/tooltip';
import {
  getBorderProps,
  getcurrentDeviceType,
  getNewMarginStyles,
  getPaddingStyles,
} from '../../../utils/helper';
import {
  paddingFields,
  paddingProps,
  PaddingProps,
} from '../../../puck/reusable-props/padding';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../../puck/reusable-props/Border';
import { backgroungFields } from '../../../puck/reusable-props/backgroungFields';
import showComponent, {
  showComponentProps,
} from '../../../puck/reusable-props/showComponent';
import WithAccordion from '../../customComponents/CustomAccordion';
import ColorPicker from '../../../puck/reusable-props/ColorPicker';
import { Select } from '../../form-fields/Select';
import {
  fontSizeOptions,
  fontWeightOptions,
} from '../../../puck/reusable-props/font';
import WidthAndHeight, {
  WidthAndHeightSubProps,
} from '../../../puck/reusable-props/widthAndHeight';
import { QuillTextEditor } from '../../../puck/reusable-props/quill-editor';
import '../../text-editor/style.scss';
import { fontFamilies } from '../../../puck/reusable-props/options';
import { JustifyOptions } from '../../blocks/Container/justify-content';
import { setSelectedVariantForSingleProduct } from '../../../redux/slices/postUpsellSlice';

export type ProductVarientSelectionProps = {
  border: IBorderValueProps;
  customizeText: string;
  customOptionText: {
    textColor: string;
    fontSize: string;
    fontWeight: string;
    fontFamily: string;
  };
  customizeButton: {
    widthAndHeight: WidthAndHeightSubProps;
    justifyContent: string;
  };
  dropdownArrow: {
    arrowColor: string;
    showArrow: boolean;
    arrowSize: number;
  };
  selectedItem: {
    backgroundColor: string;
    textColor: string;
  };
  background: {
    color: string;
    isInherit: boolean;
  };
} & MarginProps &
  PaddingProps &
  showComponentProps;
export const ProductVarientSelection = ({
  useAppSelector,
  useAppDispatch,
}: ReduxSelectorProps &
  ReduxDispatchProps): ComponentConfig<ProductVarientSelectionProps> => {
  return {
    label: (
      <CustomTooltip title="ProductVariantSelection">
        <span>Product Variant Selection</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      ...marginFields,
      ...paddingFields,
      ...backgroungFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      customizeButton: {
        type: 'custom',
        label: 'Button Customize',
        render: ({ value, onChange }) => {
          return (
            <>
              <WithAccordion title="Button Customization">
                <div className="my-2">
                  <WidthAndHeight
                    value={value.widthAndHeight}
                    onChange={val => {
                      onChange({
                        ...value,
                        widthAndHeight: val,
                      });
                    }}
                    title={'Height & Width'}
                  />
                </div>
                <FieldLabel
                  label={
                    (
                      <CustomTooltip title="Justify Content">
                        <span>Justify Content</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }>
                  <Select
                    className="pagebuilder-theme"
                    value={value?.justifyContent}
                    onChange={e => {
                      const selectedValue = e.target.value;
                      onChange({
                        ...value,
                        justifyContent: selectedValue,
                      });
                    }}
                    options={JustifyOptions}
                  />
                </FieldLabel>
              </WithAccordion>
            </>
          );
        },
      },

      customizeText: { ...QuillTextEditor('Custom Text', false).content },

      customOptionText: {
        type: 'custom',
        label: 'Text Styling',
        render: ({ value, onChange }) => {
          return (
            <WithAccordion title="Custom Option Text">
              <>
                <ColorPicker
                  value={value?.textColor}
                  onChange={color =>
                    onChange({
                      ...value,
                      textColor: color,
                    })
                  }
                  label={'Text Color'}
                  tooltip={'textColor'}
                />
                <FieldLabel
                  label={
                    (
                      <CustomTooltip title="fontSize">
                        <span>Font Size</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }>
                  <Select
                    className="pagebuilder-theme"
                    value={value?.fontSize}
                    onChange={e =>
                      onChange({
                        ...value,
                        fontSize: e.target.value,
                      })
                    }
                    options={fontSizeOptions}
                  />
                </FieldLabel>
                <FieldLabel
                  label={
                    (
                      <CustomTooltip title="fontWeight">
                        <span>Font Weight</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }>
                  <Select
                    value={value?.fontWeight}
                    onChange={e =>
                      onChange({
                        ...value,
                        fontWeight: e.target.value,
                      })
                    }
                    options={fontWeightOptions}
                  />
                </FieldLabel>
                <FieldLabel
                  label={
                    (
                      <CustomTooltip title="fontWeight">
                        <span>Font Family</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }
                  el="div">
                  <Select
                    value={value?.fontFamily}
                    onChange={event =>
                      onChange({
                        ...value,
                        fontFamily: event.target.value,
                      })
                    }
                    options={fontFamilies}
                  />
                </FieldLabel>
              </>
            </WithAccordion>
          );
        },
      },
      dropdownArrow: {
        type: 'custom',
        label: 'Dropdown Arrow',
        render: ({ value, onChange }) => {
          return (
            <WithAccordion title="Dropdown Arrow">
              <>
                <ColorPicker
                  value={value?.arrowColor}
                  onChange={color =>
                    onChange({
                      ...value,
                      arrowColor: color,
                    })
                  }
                  label={'Arrow Color'}
                  tooltip={'arrowColor'}
                />
                <div className="mb-4 mt-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={value?.showArrow}
                      onChange={e =>
                        onChange({
                          ...value,
                          showArrow: e.target.checked,
                        })
                      }
                      className="mr-2 !appearance-auto"
                    />
                    <span className="text-sm">Show Arrow</span>
                  </label>
                </div>
                <div className="mb-4">
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip title="arrowSize">
                          <span>Arrow Size</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }>
                    <input
                      type="number"
                      value={value?.arrowSize || 16}
                      onChange={e =>
                        onChange({
                          ...value,
                          arrowSize: Number(e.target.value),
                        })
                      }
                      className="w-full border rounded px-2 py-1 text-sm"
                    />
                  </FieldLabel>
                </div>
              </>
            </WithAccordion>
          );
        },
      },
      selectedItem: {
        type: 'custom',
        label: 'Selected Item Color',
        render: ({ value, onChange }) => {
          return (
            <WithAccordion title="Selected Item Color">
              <>
                <ColorPicker
                  value={value?.backgroundColor}
                  onChange={color =>
                    onChange({
                      ...value,
                      backgroundColor: color,
                    })
                  }
                  label={'Background Color'}
                  tooltip={'backgroundColor'}
                />
                <div className="mt-4">
                  <ColorPicker
                    value={value?.textColor}
                    onChange={color =>
                      onChange({
                        ...value,
                        textColor: color,
                      })
                    }
                    label={'Text Color'}
                    tooltip={'textColor'}
                  />
                </div>
              </>
            </WithAccordion>
          );
        },
      },
    },
    defaultProps: {
      customizeButton: {
        widthAndHeight: {
          height: {
            desktop: '100%',
            mobile: '100%',
            tablet: '100%',
          },
          maxWidth: {
            desktop: '100%',
            mobile: '100%',
            tablet: '100%',
          },
          deviceType: 'desktop',
        },
        justifyContent: 'justify-between',
      },
      showComponent: ['desktop', 'tablet', 'mobile'],
      margin: {
        deviceType: 'desktop',
        marginBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      customizeText:
        '<p style="line-height:1.5;"><span style="font-size: 16px; color: #4B5563; font-weight: 400;">Select a variant</span></p>',
      customOptionText: {
        textColor: '#4B5563',
        fontSize: 'text-sm',
        fontWeight: 'font-normal',
        fontFamily: 'font-sans',
      },
      dropdownArrow: {
        arrowColor: '#4B5563',
        showArrow: true,
        arrowSize: 16,
      },
      selectedItem: {
        backgroundColor: '#2563EB',
        textColor: '#FFFFFF',
      },
      background: {
        color: '#ffffff',
        isInherit: true,
      },
      ...paddingProps,
      ...borderProps,
    },
    render: ({
      margin,
      border,
      padding,
      showComponent,
      customizeButton,
      customOptionText,
      customizeText,
      dropdownArrow,
      selectedItem,
      background,
      editMode,
    }) => {
      // Track whether a selection has been made
      const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
      const [isOpen, setIsOpen] = useState(false);
      const dropdownRef = useRef<HTMLDivElement>(null);
      const currentUpsellData = useAppSelector(
        state => state.postUpSell.currentUpsellData,
      );
      const selectedVariantForSingleProduct = useAppSelector(
        state => state.postUpSell.selectedVariantForSingleProduct,
      );
      const dispatch = useAppDispatch();

      // Default hover effect colors
      const defaultHoverBgColor = '#F3F4F6'; // Light gray
      const defaultHoverTextColor = '#111827'; // Dark gray/almost black
      const currentWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(currentWidth, editMode);
      const marginStyles = getNewMarginStyles(margin, currentDeviceType);

      const toggleDropdown = () => {
        setIsOpen(!isOpen);
      };

      // Close dropdown when clicking outside
      useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
          if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target as Node)
          ) {
            setIsOpen(false);
          }
        };
        dispatch(
          setSelectedVariantForSingleProduct(
            currentUpsellData?.products?.[0]?.variants?.[0]?.productVariants,
          ),
        );

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }, []);

      const buttonStyle: React.CSSProperties =
        currentDeviceType &&
        customizeButton.widthAndHeight.height &&
        customizeButton.widthAndHeight.height[currentDeviceType] !== undefined
          ? {
              width: isNaN(
                +customizeButton.widthAndHeight.maxWidth[currentDeviceType],
              )
                ? customizeButton.widthAndHeight.maxWidth[currentDeviceType]
                : `${+customizeButton.widthAndHeight.maxWidth[currentDeviceType]}px`,
              height: isNaN(
                +customizeButton.widthAndHeight.height[currentDeviceType],
              )
                ? customizeButton.widthAndHeight.height[currentDeviceType]
                : `${+customizeButton.widthAndHeight.height[currentDeviceType]}px`,
              display: 'flex',
              alignItems: 'center',
            }
          : {
              display: 'flex',
              alignItems: 'center',
            };

      return (
        <>
          {showComponent?.includes(currentDeviceType) && (
            <div
              ref={dropdownRef}
              style={{
                ...marginStyles,
                ...getPaddingStyles(padding, currentDeviceType),
                ...getBorderProps(border, currentDeviceType),
                backgroundColor: background?.isInherit
                  ? 'inherit'
                  : background?.color,
              }}
              className={`relative w-full ${border?.borderStyle?.[currentDeviceType]}`}>
              {/* Dropdown button */}
              <div
                className={`relative cursor-pointer ${border?.borderStyle?.[currentDeviceType]}`}
                onClick={toggleDropdown}
                style={{
                  ...buttonStyle,
                }}>
                <div
                  className={`flex items-center ${customizeButton.justifyContent} w-full px-4 py-2 text-left checkbox-content`}>
                  {selectedVariantForSingleProduct?.label || (
                    <span dangerouslySetInnerHTML={{ __html: customizeText }} />
                  )}
                  {dropdownArrow?.showArrow && (
                    <svg
                      style={{
                        width: `${dropdownArrow?.arrowSize || 16}px`,
                        height: `${dropdownArrow?.arrowSize || 16}px`,
                      }}
                      fill="none"
                      stroke={dropdownArrow?.arrowColor || 'currentColor'}
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d={isOpen ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
                      />
                    </svg>
                  )}
                </div>
              </div>

              {/* Dropdown options */}
              {isOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg overflow-y-auto max-h-64">
                  {currentUpsellData?.products[0]?.variants?.map(
                    (option, index) => {
                      const isSelectedOption =
                        option?.productVariants?.value ===
                        selectedVariantForSingleProduct?.value;
                      const isHovered = hoveredIndex === index;

                      return (
                        <div
                          key={index}
                          className={`px-4 py-2 cursor-pointer transition-colors duration-150 ease-in-out ${
                            customOptionText?.fontSize || 'text-sm'
                          } ${customOptionText?.fontWeight || 'font-normal'} ${
                            customOptionText?.fontFamily || 'font-sans'
                          }`}
                          style={{
                            backgroundColor: isSelectedOption
                              ? selectedItem?.backgroundColor || '#2563EB'
                              : isHovered
                                ? defaultHoverBgColor
                                : background?.isInherit
                                  ? 'inherit'
                                  : background?.color || 'transparent',
                            color: isSelectedOption
                              ? selectedItem?.textColor || '#FFFFFF'
                              : isHovered
                                ? defaultHoverTextColor
                                : customOptionText?.textColor,
                            fontSize: customOptionText?.fontSize,
                            fontWeight: customOptionText?.fontWeight,
                            fontFamily: customOptionText?.fontFamily,
                          }}
                          onClick={() => {
                            dispatch(
                              setSelectedVariantForSingleProduct(
                                option.productVariants,
                              ),
                            );
                            setIsOpen(false);
                          }}
                          onMouseEnter={() => setHoveredIndex(index)}
                          onMouseLeave={() => setHoveredIndex(null)}>
                          {option.productVariants.label}
                        </div>
                      );
                    },
                  )}
                </div>
              )}
            </div>
          )}
        </>
      );
    },
  };
};

export default ProductVarientSelection;
